import * as Sentry from '@sentry/angular';
import { getGraphQLStyleErrorMessage } from '@jepto/shared/utils/graphql-style-error-message.util';
import { error as loggerError } from '@jepto/shared/utils/logger';

function normalizeForCapture(err: unknown, context: string): Error {
	if (err instanceof Error) {
		return err;
	}
	const gqlMessage = getGraphQLStyleErrorMessage(err);
	if (gqlMessage !== null) {
		return new Error(`${context}: ${gqlMessage}`);
	}
	if (typeof err === 'object' && err !== null) {
		const message = (err as { message?: string }).message;
		return new Error(
			message
				? `${context}: ${message}`
				: `${context}: ${JSON.stringify(err)}`
		);
	}
	return new Error(`${context}: ${String(err)}`);
}

function isExpectedViewerAccessDenial(err: unknown): boolean {
	const message =
		err instanceof Error ? err.message : typeof err === 'string' ? err : '';
	return /access denied/i.test(message);
}

/**
 * Logs to console + Sentry Logs (via shared logger) and creates a Sentry Issue.
 * Analytics-only — do not move to shared logger (would affect Jepto).
 */
export function reportAnalyticsError(
	context: string,
	err: unknown,
	tags?: Record<string, string>
): void {
	loggerError(context, err);
	if (isExpectedViewerAccessDenial(err)) {
		return;
	}
	try {
		const toCapture = normalizeForCapture(err, context);
		const hasClient =
			typeof Sentry.getClient === 'function' && !!Sentry.getClient();
		if (!hasClient) {
			return;
		}
		Sentry.captureException(toCapture, {
			...(tags ? { tags } : {}),
			extra: { context },
		});
	} catch (captureError) {
		console.warn(
			'[Analytics][Sentry] captureException failed',
			captureError
		);
	}
}
